import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageCircle, X, ArrowRight, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { logger } from '../lib/logger';
import { toast } from 'sonner';

/**
 * QuickMindsetHelpWidget (Phase 30)
 *
 * Homepage lead-gen chat bubble. Coach Varsay Lewis voice. Strictly a
 * conversion tool — answers 2 free questions, captures the lead before Q3,
 * then directs to the full Apex AI Coach.
 *
 * Cost rails (all enforced server-side, this component just renders state):
 *   • Q1 is canned (no LLM)
 *   • Q2/Q3 may hit canned cache, deflection, or Gemini 2.5 Flash
 *   • Daily $1 spend cap + 5 sessions/IP/24h
 *   • Hard cap: 3 questions per session
 *
 * Routes the user to /app/login (Apex AI Coach entry) once the chat closes.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const FULL_COACH_HREF = '/app/login';

// One session-id per browser tab. Survives modal close/reopen so the
// question count is preserved.
const SESSION_STORAGE_KEY = 'apex.quickMindsetHelp.sid.v1';
const STAGE_STORAGE_KEY = 'apex.quickMindsetHelp.stage.v1';
// Persists across browser sessions so a returning visitor on the same device
// can be auto-recognised by the hybrid stage inference (Phase 32).
const KNOWN_EMAIL_STORAGE_KEY = 'apex.quickMindsetHelp.email.v1';
// Phase 33 — once a visitor has opened the bubble at least once, suppress
// the pulse-ring "I'm here" animation forever on this device. They know it
// exists; nudging them again would feel pestery.
const HAS_OPENED_STORAGE_KEY = 'apex.quickMindsetHelp.opened.v1';
const getSessionId = () => {
  if (typeof window === 'undefined') return `s_${Math.random().toString(36).slice(2, 14)}`;
  try {
    let sid = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
    }
    return sid;
  } catch (e) {
    logger.debug('QuickMindsetHelp session-id storage blocked', e);
    return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
};

const getStoredStage = () => {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem(STAGE_STORAGE_KEY);
    return ['new', 'building', 'advanced'].includes(v) ? v : null;
  } catch (e) {
    logger.debug('QuickMindsetHelp stage storage blocked', e);
    return null;
  }
};

const persistStage = (stage) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STAGE_STORAGE_KEY, stage);
  } catch (e) {
    logger.debug('QuickMindsetHelp stage persist blocked', e);
  }
};

const getKnownEmail = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(KNOWN_EMAIL_STORAGE_KEY) || null;
  } catch (e) {
    logger.debug('QuickMindsetHelp known-email read blocked', e);
    return null;
  }
};

const persistKnownEmail = (email) => {
  if (typeof window === 'undefined' || !email) return;
  try {
    window.localStorage.setItem(KNOWN_EMAIL_STORAGE_KEY, email);
  } catch (e) {
    logger.debug('QuickMindsetHelp known-email persist blocked', e);
  }
};

const getHasOpened = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(HAS_OPENED_STORAGE_KEY) === '1';
  } catch (e) {
    logger.debug('QuickMindsetHelp open-flag read blocked', e);
    return false;
  }
};

const markOpened = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(HAS_OPENED_STORAGE_KEY, '1');
  } catch (e) {
    logger.debug('QuickMindsetHelp open-flag persist blocked', e);
  }
};

const STAGE_LABELS = {
  new: 'New here',
  building: 'Building',
  advanced: 'Advanced',
};

// The 3-pill selector — Coach Lewis spec: ask ONCE at session start.
const STAGE_OPTIONS = [
  {
    id: 'new',
    title: 'New here',
    blurb: 'First time. Walk me through it.',
  },
  {
    id: 'building',
    title: 'Building',
    blurb: '3–6 weeks in. Skip the warm-up.',
  },
  {
    id: 'advanced',
    title: 'Advanced',
    blurb: '7+ weeks. Cues only.',
  },
];

// Routes where the bubble should NOT show — anywhere the visitor is mid-
// flow or already inside the real Coach AI app.
const EXCLUDED_PATH_PREFIXES = ['/app/', '/admin/', '/auth/', '/assessment', '/book'];

const shouldExcludePath = (pathname) => {
  if (!pathname) return false;
  return EXCLUDED_PATH_PREFIXES.some((p) => pathname === p.replace(/\/$/, '') || pathname.startsWith(p));
};

const CoachLewisBadge = () => (
  <div className="flex items-center gap-2">
    <div className="h-8 w-8 rounded-full bg-[#004C54] text-white flex items-center justify-center font-heading font-bold text-[12px]">
      VL
    </div>
    <div>
      <div className="text-[12.5px] font-heading font-bold">Coach Varsay Lewis</div>
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-mono-accent text-foreground/55">
        Quick mindset help
      </div>
    </div>
  </div>
);

export default function QuickMindsetHelpWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // { role: 'user'|'coach', text }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [questionsUsed, setQuestionsUsed] = useState(0);
  const [needsCapture, setNeedsCapture] = useState(false);
  const [conversationDone, setConversationDone] = useState(false);
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [submittingLead, setSubmittingLead] = useState(false);
  // Coach Lewis spec: ask "first time or returning?" ONCE per session and
  // persist the answer so the same browser tab doesn't get re-asked.
  const [stage, setStage] = useState(() => getStoredStage());
  // Phase 32 hybrid recognition — if the backend confidently identifies the
  // visitor we skip the picker and show a one-line "we've got you logged
  // as..." banner with a Change link.
  const [recognition, setRecognition] = useState(null); // { stage, confidence, display_name }
  const [profileChecked, setProfileChecked] = useState(false);
  // Phase 33 discoverability — pulse ring on first session only, suppressed
  // forever once the visitor opens the bubble at least once.
  const [hasEverOpened, setHasEverOpened] = useState(() => getHasOpened());
  const sessionIdRef = useRef(null);
  const scrollerRef = useRef(null);

  if (sessionIdRef.current == null) sessionIdRef.current = getSessionId();

  const openModal = useCallback(() => {
    setOpen(true);
    if (!hasEverOpened) {
      markOpened();
      setHasEverOpened(true);
    }
  }, [hasEverOpened]);

  const pickStage = useCallback((nextStage) => {
    setStage(nextStage);
    persistStage(nextStage);
  }, []);

  const overrideStage = useCallback(() => {
    // Visitor disagreed with our recognition — fall back to the picker.
    setStage(null);
    setRecognition(null);
    persistStage('');
  }, []);

  // Hide on excluded routes (re-check on every render — pathname is read live).
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const hidden = shouldExcludePath(pathname);

  // Auto-scroll on new messages.
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open]);

  // Phase 32 — on modal open, ask the backend if we can recognise the
  // visitor (auth cookie OR a previously-captured email from localStorage).
  // High/medium confidence skips the picker; low/none falls back to it.
  useEffect(() => {
    if (!open || profileChecked) return;
    let cancelled = false;
    (async () => {
      try {
        const knownEmail = getKnownEmail();
        const qs = new URLSearchParams();
        if (sessionIdRef.current) qs.set('session_id', sessionIdRef.current);
        if (knownEmail) qs.set('email', knownEmail);
        const res = await fetch(`${BACKEND_URL}/api/lead-gen/profile?${qs.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const conf = data.confidence;
        const recommended = data.recommended_stage;
        // Honour any explicit prior pick in this tab even if the backend
        // returns a different recommendation.
        const tabStage = getStoredStage();
        if (tabStage) {
          setStage(tabStage);
        } else if (
          (conf === 'high' || conf === 'medium') &&
          ['new', 'building', 'advanced'].includes(recommended)
        ) {
          setStage(recommended);
          persistStage(recommended);
          setRecognition({
            stage: recommended,
            confidence: conf,
            display_name: data.display_name || null,
          });
        }
      } catch (err) {
        logger.debug('QuickMindsetHelp profile lookup failed', err);
        // Soft fail — picker will show as fallback.
      } finally {
        if (!cancelled) setProfileChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, profileChecked]);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  // Click-outside dismiss
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) closeModal();
  };

  const sendQuestion = useCallback(async () => {
    const q = input.trim();
    if (!q || sending) return;
    setSending(true);
    setMessages((prev) => [...prev, { role: 'user', text: q }]);
    setInput('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-gen/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          question: q,
          stage: stage || 'new',
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'coach', text: data.answer, source: data.source }]);
      setQuestionsUsed(data.questions_used || 0);
      setNeedsCapture(!!data.needs_lead_capture && !leadCaptured);
      setConversationDone(!!data.conversation_done);
    } catch (err) {
      logger.warn('QuickMindsetHelp chat failed', err);
      setMessages((prev) => [
        ...prev,
        { role: 'coach', text: "We hit a snag on our end — give it another try in a moment." },
      ]);
    } finally {
      setSending(false);
    }
  }, [input, sending, leadCaptured, stage]);

  const submitLead = async (e) => {
    e.preventDefault();
    if (submittingLead) return;
    const name = leadName.trim();
    const email = leadEmail.trim();
    if (!name || !email) {
      toast.error('Need both your name and email to keep going');
      return;
    }
    setSubmittingLead(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/lead-gen/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionIdRef.current, name, email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setLeadCaptured(true);
      setNeedsCapture(false);
      // Remember this email across browser sessions so the *next* visit can
      // be auto-recognised by /api/lead-gen/profile (Phase 32 hybrid).
      persistKnownEmail(email);
      setMessages((prev) => [
        ...prev,
        {
          role: 'coach',
          text: `Got it, ${name.split(' ')[0]}. One more question and then let's take this into the full Apex AI Coach where the deeper work lives.`,
        },
      ]);
    } catch (err) {
      logger.warn('QuickMindsetHelp capture failed', err);
      toast.error(err.message || 'Could not save your info — try again');
    } finally {
      setSubmittingLead(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion();
    }
  };

  const showCTAFooter = useMemo(
    () => conversationDone || (questionsUsed >= 3),
    [conversationDone, questionsUsed]
  );

  if (hidden) return null;

  return (
    <>
      {/* Floating trigger bubble (Phase 33 — teal brand color, gentle pulse
          ring on first-ever visit only). The ring uses keyframe animations
          defined in /app/frontend/src/index.css. Pulse stops the moment the
          visitor has opened the bubble at least once on this device. */}
      {!open && (
        <button
          type="button"
          onClick={openModal}
          aria-label="Quick Mindset Help — open chat"
          data-testid="quick-mindset-help-trigger"
          className="group fixed bottom-5 right-5 z-[80] inline-flex items-center gap-1.5 rounded-full bg-[#004C54] text-white px-4 h-12 shadow-lg shadow-[#004C54]/30 hover:shadow-2xl hover:bg-[#003a40] transition-all hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004C54] focus-visible:ring-offset-2"
        >
          {!hasEverOpened && (
            <>
              {/* Two staggered rings give a slow heartbeat feel without
                  ever feeling pushy. Pointer-events:none so they never
                  swallow the click. */}
              <span
                aria-hidden="true"
                className="qmh-pulse-ring pointer-events-none absolute inset-0 rounded-full"
              />
              <span
                aria-hidden="true"
                className="qmh-pulse-ring qmh-pulse-ring--delayed pointer-events-none absolute inset-0 rounded-full"
              />
            </>
          )}
          <MessageCircle className="relative h-[22px] w-[22px] text-white group-hover:animate-pulse" />
          <span className="relative hidden sm:inline text-[14px] font-semibold tracking-tight text-white/95 group-hover:text-white">
            Quick Mindset Help
          </span>
        </button>
      )}

      {/* Chat modal */}
      {open && (
        <div
          className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
          onClick={onBackdropClick}
          data-testid="quick-mindset-help-modal"
        >
          <div className="w-full sm:max-w-md bg-background rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[85vh] sm:max-h-[640px]">
            {/* Header */}
            <div className="px-4 sm:px-5 py-3 border-b border-border/60 flex items-center justify-between gap-3">
              <CoachLewisBadge />
              <button
                onClick={closeModal}
                aria-label="Close chat"
                className="p-2 rounded-lg hover:bg-secondary/60"
                data-testid="quick-mindset-help-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Stage selector — Coach Lewis spec: ask ONCE at session start.
                Stays out of the way once chosen. Skipped when the backend
                auto-recognises the visitor (see recognition banner below). */}
            {!stage && (
              <div
                className="px-4 sm:px-5 py-5 border-b border-border/60 bg-secondary/30"
                data-testid="quick-mindset-help-stage-picker"
              >
                <div className="text-[13px] font-semibold text-foreground/80 mb-3">
                  Quick check &mdash; first time here or returning?
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {STAGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => pickStage(opt.id)}
                      data-testid={`quick-mindset-help-stage-${opt.id}`}
                      className="text-left p-3 rounded-lg border border-border/70 bg-background hover:border-[#004C54] hover:bg-[#004C54]/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004C54]/50 transition-colors"
                    >
                      <div className="text-[12.5px] font-heading font-bold text-foreground">
                        {opt.title}
                      </div>
                      <div className="text-[11.5px] text-foreground/60 mt-0.5">
                        {opt.blurb}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-recognition banner — appears only when the backend
                confidently identified the visitor. Visitor can override. */}
            {stage && recognition && (
              <div
                className="px-4 sm:px-5 py-2.5 border-b border-border/60 bg-[#004C54]/5 flex items-center justify-between gap-3"
                data-testid="quick-mindset-help-recognition-banner"
              >
                <div className="text-[12px] text-foreground/75 leading-snug">
                  {recognition.display_name ? (
                    <>Welcome back, <strong>{recognition.display_name}</strong>. </>
                  ) : (
                    <>Welcome back. </>
                  )}
                  We&rsquo;ve got you logged as a{' '}
                  <strong>{STAGE_LABELS[recognition.stage] || recognition.stage}</strong>{' '}
                  athlete.
                </div>
                <button
                  type="button"
                  onClick={overrideStage}
                  data-testid="quick-mindset-help-change-level"
                  className="text-[12px] font-semibold text-[#004C54] hover:underline shrink-0"
                >
                  Change level
                </button>
              </div>
            )}

            {/* Message scroller */}
            <div
              ref={scrollerRef}
              className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-3"
              data-testid="quick-mindset-help-messages"
            >
              {messages.length === 0 && (
                <div className="text-[13.5px] text-foreground/70 leading-relaxed">
                  Drop a quick question about <strong>game-day nerves</strong>, <strong>focus drift</strong>, or a <strong>confidence dip</strong>. We&rsquo;ve got a couple of minutes for you here &mdash; then we&rsquo;ll take the deeper work into the full Coach AI.
                </div>
              )}
              {messages.map((m, i) => {
                const isUser = m.role === 'user';
                return (
                  <div
                    key={`${m.role}-${i}-${(m.text || '').slice(0, 12)}`}
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed whitespace-pre-wrap ${
                        isUser
                          ? 'bg-[#004C54] text-white rounded-br-md'
                          : 'bg-secondary/70 text-foreground rounded-bl-md'
                      }`}
                    >
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {sending && (
                <div className="flex justify-start">
                  <div className="px-3.5 py-2.5 rounded-2xl bg-secondary/70 inline-flex items-center gap-2 text-[12px] text-foreground/65">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Coach is thinking…
                  </div>
                </div>
              )}
            </div>

            {/* Lead capture form — appears between Q2 and Q3 */}
            {needsCapture && !leadCaptured && (
              <form
                onSubmit={submitLead}
                className="px-4 sm:px-5 py-3 border-t border-border/60 bg-secondary/30 space-y-2"
                data-testid="quick-mindset-help-capture-form"
              >
                <div className="text-[12.5px] font-semibold text-foreground/80">
                  Drop your name and email &mdash; we&rsquo;ll keep going.
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="qmh-name" className="sr-only">Name</Label>
                    <Input
                      id="qmh-name"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      placeholder="First name"
                      autoComplete="given-name"
                      data-testid="quick-mindset-help-name"
                      className="h-9 text-[13px]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="qmh-email" className="sr-only">Email</Label>
                    <Input
                      id="qmh-email"
                      type="email"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      placeholder="Email"
                      autoComplete="email"
                      data-testid="quick-mindset-help-email"
                      className="h-9 text-[13px]"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submittingLead || !leadName.trim() || !leadEmail.trim()}
                  className="w-full h-9 bg-[#004C54] hover:bg-[#003a40] text-white text-[13px]"
                  data-testid="quick-mindset-help-capture-submit"
                >
                  {submittingLead ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…</> : 'Send it'}
                </Button>
              </form>
            )}

            {/* Composer or done-state CTA */}
            {showCTAFooter ? (
              <div className="px-4 sm:px-5 py-3 border-t border-border/60 bg-secondary/30">
                <a
                  href={FULL_COACH_HREF}
                  className="flex items-center justify-center gap-2 w-full h-11 rounded-lg bg-[#004C54] hover:bg-[#003a40] text-white font-semibold text-[14px] transition-colors"
                  data-testid="quick-mindset-help-cta"
                >
                  Access Full Apex AI Coach
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            ) : (
              <div className="px-4 sm:px-5 py-3 border-t border-border/60 flex items-end gap-2">
                <textarea
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={stage ? 'Type a quick question…' : 'Pick a level above to start…'}
                  disabled={sending || needsCapture || !stage}
                  className="flex-1 resize-none rounded-lg border border-border/60 bg-background px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#004C54] focus:border-transparent disabled:opacity-50"
                  data-testid="quick-mindset-help-input"
                />
                <Button
                  onClick={sendQuestion}
                  disabled={sending || !input.trim() || needsCapture || !stage}
                  size="sm"
                  className="h-9 bg-[#004C54] hover:bg-[#003a40] text-white"
                  data-testid="quick-mindset-help-send"
                >
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
